import axios from 'axios';
import { getToken, clearToken } from './auth';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      clearToken();
      // Hard-redirect to /login once per page-load so a mid-session token
      // expiry doesn't leave the user stuck on a broken screen. Skip if
      // already on an auth route or if we've already handled it this cycle.
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        const onAuthRoute = path === '/login' || path === '/signup';
        if (!onAuthRoute && !window.__lmsSessionExpiredHandled) {
          window.__lmsSessionExpiredHandled = true;
          try { window.sessionStorage.setItem('lms_session_expired', '1'); } catch { /* ignore */ }
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Courses
export const getNavigationLayout = () => api.get('/admin/navigation-layout');
export const updateNavigationLayout = (data) => api.put('/admin/navigation-layout', data);

export const getCourses = (params) => api.get('/courses', { params });
export const getCourseRefs = () => api.get('/courses/refs');
export const getCourse = (id) => api.get(`/courses/${id}`);
export const createCourse = (data) => api.post('/courses', data);
export const updateCourse = (id, data) => api.put(`/courses/${id}`, data);
export const deleteCourse = (id) => api.delete(`/courses/${id}`);
export const enrollStudent = (courseId, studentId) => api.post(`/courses/${courseId}/enroll/${studentId}`);
export const unenrollStudent = (courseId, studentId) => api.post(`/courses/${courseId}/unenroll/${studentId}`);

// Course modules (units / weeks that group lessons)
export const getCourseModules = (courseId) => api.get(`/courses/${courseId}/modules`);
export const createCourseModule = (courseId, data) => api.post(`/courses/${courseId}/modules`, data);
export const updateCourseModule = (courseId, moduleId, data) =>
  api.put(`/courses/${courseId}/modules/${moduleId}`, data);
export const deleteCourseModule = (courseId, moduleId) =>
  api.delete(`/courses/${courseId}/modules/${moduleId}`);

// Lessons
export const getLessons = (params) => api.get('/lessons', { params });
export const getLesson = (id) => api.get(`/lessons/${id}`);
export const createLesson = (data) => api.post('/lessons', data);
export const updateLesson = (id, data) => api.put(`/lessons/${id}`, data);
export const deleteLesson = (id) => api.delete(`/lessons/${id}`);
export const reorderCourseLessons = (courseId, items) =>
  api.put(`/courses/${courseId}/lessons/reorder`, { items });

// Resequence the whole classwork stream — materials, assignments and quizzes
// together. `items` are { item_id, kind, module_id, order }.
export const reorderCourseClasswork = (courseId, items) =>
  api.put(`/courses/${courseId}/classwork/reorder`, { items });

// Lesson media — the browser uploads straight to R2 with a presigned PUT so
// file bytes never pass through the API.
export const createLessonUploadUrl = (lessonId, data) => api.post(`/lessons/${lessonId}/uploads`, data);
export const attachLessonMaterial = (lessonId, data) => api.post(`/lessons/${lessonId}/materials`, data);
export const deleteLessonMaterial = (lessonId, materialId) =>
  api.delete(`/lessons/${lessonId}/materials/${materialId}`);
export const getLessonMaterialUrl = (lessonId, materialId) =>
  api.get(`/lessons/${lessonId}/materials/${materialId}/url`);
export const attachLessonVideo = (lessonId, data) => api.post(`/lessons/${lessonId}/video`, data);
export const getLessonVideoUrl = (lessonId) => api.get(`/lessons/${lessonId}/video/url`);

/**
 * Upload one file to R2 and attach it to a lesson.
 *
 * Three steps: ask the API to presign a PUT, send the raw file to R2, then
 * confirm the attach so the server can verify the real object size before it
 * records the key. `onProgress` receives 0-100.
 */
export async function uploadLessonFile(lessonId, file, { kind = 'material', title = '', onProgress } = {}) {
  const { data: presigned } = await createLessonUploadUrl(lessonId, {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size: file.size,
    kind,
  });

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.upload_url, true);
    xhr.setRequestHeader('Content-Type', presigned.content_type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () =>
      (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });

  const payload = {
    key: presigned.key,
    filename: file.name,
    content_type: presigned.content_type,
    ...(kind === 'material' ? { title } : {}),
  };
  return kind === 'video'
    ? attachLessonVideo(lessonId, payload)
    : attachLessonMaterial(lessonId, payload);
}

// Exams
export const getExams = (params) => api.get('/exams', { params });
export const getExam = (id) => api.get(`/exams/${id}`);
export const createExam = (data) => api.post('/exams', data);
export const updateExam = (id, data) => api.put(`/exams/${id}`, data);
export const deleteExam = (id) => api.delete(`/exams/${id}`);

// Assignments
export const getAssignments = (params) => api.get('/assignments', { params });
export const getAssignment = (id) => api.get(`/assignments/${id}`);
export const createAssignment = (data) => api.post('/assignments', data);
export const updateAssignment = (id, data) => api.put(`/assignments/${id}`, data);
export const deleteAssignment = (id) => api.delete(`/assignments/${id}`);

// Live Sessions
export const getLiveSessions = (params) => api.get('/live-sessions', { params });
export const getLiveSession = (id) => api.get(`/live-sessions/${id}`);
export const createLiveSession = (data) => api.post('/live-sessions', data);
export const updateLiveSession = (id, data) => api.put(`/live-sessions/${id}`, data);
export const deleteLiveSession = (id) => api.delete(`/live-sessions/${id}`);
export const provisionZoomMeeting = (id) => api.post(`/live-sessions/${id}/zoom`);
export const getLiveSessionJoin = (id) => api.get(`/live-sessions/${id}/join`);
export const getZoomStatus = () => api.get('/zoom/status');
export const startLiveSession = (id) => api.post(`/live-sessions/${id}/start`);
export const endLiveSession = (id) => api.post(`/live-sessions/${id}/end`);
export const setLiveSessionRecording = (id, recordingUrl) =>
  api.put(`/live-sessions/${id}/recording`, { recording_url: recordingUrl });
// Called by the meeting room once the student is actually in the session.
export const recordLiveSessionAttendance = (id) => api.post(`/live-sessions/${id}/attendance`);

// The single course workspace both students and instructors open. The server
// decides what belongs in the payload; `role` tells the page which affordances
// to render.
export const getCourseRoom = (courseId) => api.get(`/courses/${courseId}/room`);

// Course intakes — one delivery of a program to one cohort. The sidebar tree
// is built from these; `id` is opaque and never displayed.
export const getCourseIntakes = (params) => api.get('/course-intakes', { params });
export const createCourseIntake = (data) => api.post('/course-intakes', data);
export const updateCourseIntake = (id, data) => api.put(`/course-intakes/${id}`, data);
export const deleteCourseIntake = (id) => api.delete(`/course-intakes/${id}`);
export const assignCourseIntake = (courseId, intakeId) =>
  api.put(`/courses/${courseId}/intake`, { intake_id: intakeId });
export const getMyCourseNav = () => api.get('/me/course-nav');

// Course resources — the course-level materials library. A resource is either
// an uploaded file (private bucket, presigned download) or an external link.
export const getCourseResources = (courseId, params) =>
  api.get(`/courses/${courseId}/resources`, { params });
export const createCourseResource = (courseId, data) => api.post(`/courses/${courseId}/resources`, data);
export const updateCourseResource = (courseId, resourceId, data) =>
  api.put(`/courses/${courseId}/resources/${resourceId}`, data);
export const deleteCourseResource = (courseId, resourceId) =>
  api.delete(`/courses/${courseId}/resources/${resourceId}`);
export const getCourseResourceUrl = (courseId, resourceId) =>
  api.get(`/courses/${courseId}/resources/${resourceId}/url`);
export const createCourseResourceUploadUrl = (courseId, data) =>
  api.post(`/courses/${courseId}/resources/uploads`, data);

/**
 * Upload one file to R2 and attach it to a course as a resource.
 *
 * Same three-step exchange as `uploadLessonFile`: presign, PUT the raw bytes
 * to R2, then confirm so the server verifies the real object size before it
 * records the key. `onProgress` receives 0-100.
 */
export async function uploadCourseResource(courseId, file, { title = '', description = '', itemId = '', assignmentId = '', status = 'published', onProgress } = {}) {
  const { data: presigned } = await createCourseResourceUploadUrl(courseId, {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size: file.size,
  });

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.upload_url, true);
    xhr.setRequestHeader('Content-Type', presigned.content_type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () =>
      (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });

  return createCourseResource(courseId, {
    kind: 'file',
    key: presigned.key,
    filename: file.name,
    content_type: presigned.content_type,
    title,
    description,
    item_id: itemId || assignmentId,
    status,
  });
}

// Course design reuse — save a course's teaching design as a template, or copy
// one course's design straight onto another.
export const getCourseTemplates = () => api.get('/course-designs/templates');
export const createCourseTemplate = (data) => api.post('/course-designs/templates', data);
export const deleteCourseTemplate = (id) => api.delete(`/course-designs/templates/${id}`);
export const previewCourseDesign = (courseId) => api.get(`/course-designs/preview/${courseId}`);
export const applyCourseDesign = (data) => api.post('/course-designs/apply', data);

// Students
export const getStudents = (params) => api.get('/students', { params });
export const getStudentsMeta = () => api.get('/students/meta');
export const getStudent = (id) => api.get(`/students/${id}`);
export const createStudent = (data) => api.post('/students', data);
export const updateStudent = (id, data) => api.put(`/students/${id}`, data);
export const deleteStudent = (id, params) => api.delete(`/students/${id}`, { params });
export const bulkActionStudents = (data) => api.post('/students/bulk-action', data);
export const previewStudentImport = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/students/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const commitStudentImport = (rows) => api.post('/students/import/commit', { rows });
export const addStudentNote = (id, data) => api.post(`/students/${id}/notes`, data);
export const updateStudentNote = (id, noteId, data) => api.put(`/students/${id}/notes/${noteId}`, data);
export const deleteStudentNote = (id, noteId) => api.delete(`/students/${id}/notes/${noteId}`);
export const revealStudentSin = (id) => api.get(`/students/${id}/sin`);

// Student exports
// format: 'csv' | 'pdf'
// ids: undefined | [] -> export all; otherwise export only those students
export const getStudentsExportUrl = (format, ids) => {
  const base = `${API}/students/export/${format}`;
  if (!ids || ids.length === 0) return base;
  const params = new URLSearchParams({ ids: ids.join(',') });
  return `${base}?${params.toString()}`;
};

// Authenticated file download — fetches with the JWT then triggers a save dialog.
export const triggerDownload = async (url, fallbackFilename = 'download') => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackFilename;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
};

// Applications (Admissions CRM) — admin-only management and manual intake.
export const getApplications = (params) => api.get('/applications', { params });
export const createApplicants = (rows) => api.post('/admin/applicants', { rows });
export const previewApplicantImport = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/admin/applicants/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getApplicantsExportUrl = (format, ids) => `${API}/admin/applicants/export/${format}?${new URLSearchParams({ ids: ids.join(',') })}`;
export const getApplication = (id) => api.get(`/applications/${id}`);
export const updateApplication = (id, data) => api.patch(`/applications/${id}`, data);
export const retryApplicationDocumentDrafts = (id) => api.post(`/applications/${id}/document-drafts/retry`);
export const deleteApplication = (id) => api.delete(`/applications/${id}`);
export const addApplicationNote = (id, data) => api.post(`/applications/${id}/notes`, data);
export const updateApplicationNote = (id, noteId, data) =>
  api.put(`/applications/${id}/notes/${encodeURIComponent(noteId)}`, data);
export const deleteApplicationNote = (id, noteId) =>
  api.delete(`/applications/${id}/notes/${encodeURIComponent(noteId)}`);
export const revealApplicationSin = (id) => api.get(`/applications/${id}/sin`);
export const convertApplication = (id) => api.post(`/applications/${id}/convert`);
export const revertApplicationEnrollment = (id) => api.post(`/applications/${id}/revert-enrollment`);
// Freezing an admissions record as the account of what the applicant said.
// Super admin only; the server enforces that, not the hidden button.
export const lockApplication = (id) => api.post(`/applications/${id}/lock`);
export const unlockApplication = (id) => api.post(`/applications/${id}/unlock`);
export const getApplicationOfferPackEmailPreview = (id) => api.get(`/applications/${id}/offer-pack-email`);
// Shared staff composer. The API resolves record ids to email addresses and
// merge fields server-side, then sends a separate personalized message to each.
export const sendPortalEmail = (data) => api.post('/admin/emails/send', data);
export const getEmailTemplateWorkspace = () => api.get('/admin/email-templates');
export const getPortalEmailComposeContext = (data) => api.post('/admin/emails/compose', data);
export const getPortalEmailDirectory = (q = '') =>
  api.get('/admin/email-directory', { params: q ? { q } : {} });
export const createEmailTemplate = (data) => api.post('/admin/email-templates', data);
export const updateEmailTemplate = (id, data) => api.put(`/admin/email-templates/${encodeURIComponent(id)}`, data);
export const deleteEmailTemplate = (id) => api.delete(`/admin/email-templates/${encodeURIComponent(id)}`);
export const updateEmailMergeFields = (fields) => api.put('/admin/email-merge-fields', { fields });
export const resetEmailMergeFields = () => api.delete('/admin/email-merge-fields');
// Returns { url, expires_in } — a short-lived presigned GET for one document.
export const getApplicationDocumentUrl = (id, index) =>
  api.get(`/applications/${id}/documents/${index}/url`);

// Staff add or replace an application's supporting document. Mirrors the
// backend's fallback cap: a Vercel function refuses bodies over 4.5 MB.
export const APPLICATION_DOCUMENT_API_UPLOAD_BYTES = 4 * 1024 * 1024;
export const createApplicationDocumentUploadUrl = (id, data) =>
  api.post(`/applications/${id}/documents/upload-url`, data);
export const attachApplicationDocument = (id, data) => api.post(`/applications/${id}/documents`, data);

/**
 * Upload one file onto an application, replacing the document at
 * `replaceIndex` when given. The browser PUTs straight to R2; if the bucket
 * refuses this origin, a small file goes through the API instead. Resolves to
 * the updated application.
 */
export async function uploadApplicationDocument(id, file, { docType, replaceIndex = null } = {}) {
  const replace = replaceIndex === null ? {} : { replace_index: replaceIndex };
  const { data: presigned } = await createApplicationDocumentUploadUrl(id, {
    doc_type: docType,
    filename: file.name,
    content_type: file.type,
  });
  let response;
  try {
    response = await fetch(presigned.url, { method: 'PUT', headers: presigned.headers, body: file });
  } catch {
    // A network-level failure here is the bucket's CORS policy refusing the
    // portal origin; an HTTP error from R2 would have resolved instead.
    if (file.size > APPLICATION_DOCUMENT_API_UPLOAD_BYTES) {
      throw new Error('Storage refused the upload, and this file is too large to send another way (max 4 MB).');
    }
    const form = new FormData();
    form.append('doc_type', docType);
    if (replaceIndex !== null) form.append('replace_index', String(replaceIndex));
    form.append('file', file);
    return api.post(`/applications/${id}/documents/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
  return attachApplicationDocument(id, { doc_type: docType, key: presigned.key, filename: file.name, ...replace });
}

// Agents / Agencies - admin-only.
export const getAgents = () => api.get('/agents');
// The workspace layout is shared by every admin, so these are not /me routes.
export const getAdminAgenciesWorkspaceLayout = () => api.get('/admin/agencies-workspace-layout');
export const updateAdminAgenciesWorkspaceLayout = (data) => api.put('/admin/agencies-workspace-layout', data);
export const resetAdminAgenciesWorkspaceLayout = () => api.delete('/admin/agencies-workspace-layout');
// @-mention directory: staff to notify, students and agencies to link.
export const getMentionDirectory = (q = '') =>
  api.get('/admin/mention-directory', { params: q ? { q } : {} });

// Team chat — named channels plus one-to-one conversations.
export const getChatChannels = (params) => api.get('/admin/chat/channels', { params });
export const createChatChannel = (data) => api.post('/admin/chat/channels', data);
export const openDirectChat = (userId) => api.post('/admin/chat/direct', { user_id: userId });
export const getChatMessages = (channelId, params) => api.get(`/admin/chat/channels/${channelId}/messages`, { params });
export const postChatMessage = (channelId, data) =>
  api.post(`/admin/chat/channels/${channelId}/messages`, data);

// Website signups — event leads from the marketing site. Separate from
// Admissions: these never become an application or a portal account.
export const getWebsiteSignups = (params) => api.get('/admin/website-signups', { params });
export const getWebsiteSignup = (id) => api.get(`/admin/website-signups/${id}`);
export const updateWebsiteSignup = (id, data) => api.patch(`/admin/website-signups/${id}`, data);
// Notes use the shared threaded system — same shape, mentions and notifications
// as application, student and agency notes.
export const addWebsiteSignupNote = (id, data) => api.post(`/admin/website-signups/${id}/notes`, data);
export const updateWebsiteSignupNote = (id, noteId, data) =>
  api.put(`/admin/website-signups/${id}/notes/${encodeURIComponent(noteId)}`, data);
export const deleteWebsiteSignupNote = (id, noteId) =>
  api.delete(`/admin/website-signups/${id}/notes/${encodeURIComponent(noteId)}`);
// Cancel keeps the record, the notes and the consent evidence and gives the
// seat back. Delete removes the person's data outright and is full-admin only —
// both are POSTs so the confirmation travels in a body rather than a URL.
export const cancelWebsiteSignup = (id, data) =>
  api.post(`/admin/website-signups/${id}/cancel`, data || {});
export const promoteWebsiteSignup = (id, data) =>
  api.post(`/admin/website-signups/${id}/promote`, data || {});
export const resendRegistrationConfirmation = (id) =>
  api.post(`/admin/website-signups/${id}/resend-confirmation`, {});
export const deleteWebsiteSignup = (id, data) =>
  api.post(`/admin/website-signups/${id}/delete`, data);
export const bulkDeleteWebsiteSignups = (data) =>
  api.post('/admin/website-signups/bulk-delete', data);

// Website messages — public visitor conversations. Deliberately NOT the
// internal Team Chat above; different collections, different inbox.
export const getWebsiteConversations = (params) =>
  api.get('/admin/website-chat/conversations', { params });
export const getWebsiteConversationMessages = (id) =>
  api.get(`/admin/website-chat/conversations/${id}/messages`);
export const postWebsiteConversationMessage = (id, data) =>
  api.post(`/admin/website-chat/conversations/${id}/messages`, data);
export const updateWebsiteConversation = (id, data) =>
  api.patch(`/admin/website-chat/conversations/${id}`, data);
export const sendWebsiteChatHeartbeat = () => api.post('/admin/website-chat/presence/heartbeat');
export const getWebsiteChatSettings = () => api.get('/admin/website-chat/settings');
export const updateWebsiteChatSettings = (data) => api.patch('/admin/website-chat/settings', data);
// Permanent, and full-admin only. Deleting a conversation takes its messages
// with it; deleting one message leaves the thread and recalculates its summary.
export const deleteWebsiteConversation = (id) =>
  api.delete(`/admin/website-chat/conversations/${encodeURIComponent(id)}`);
export const deleteWebsiteConversationMessage = (id, messageId) =>
  api.delete(
    `/admin/website-chat/conversations/${encodeURIComponent(id)}/messages/${encodeURIComponent(messageId)}`,
  );

// Website Engagement landing page — counts for all four inboxes in one call.
export const getWebsiteEngagementSummary = () => api.get('/admin/website-engagement/summary');

// Contact inquiries — the public contact form. Separate from the live-chat
// conversations above: one message answered by email, not a session.
export const getContactInquiries = (params) => api.get('/admin/contact-inquiries', { params });
export const getContactInquiry = (id) => api.get(`/admin/contact-inquiries/${encodeURIComponent(id)}`);
// Status, owner and read state. "replied" is written by a successful send, not
// from here, and the server refuses it.
export const updateContactInquiry = (id, data) =>
  api.patch(`/admin/contact-inquiries/${encodeURIComponent(id)}`, data);
// Notes use the shared threaded system — same shape, mentions and
// notifications as application, student, agency and signup notes.
export const addContactInquiryNote = (id, data) =>
  api.post(`/admin/contact-inquiries/${encodeURIComponent(id)}/notes`, data);
export const updateContactInquiryNote = (id, noteId, data) =>
  api.put(`/admin/contact-inquiries/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, data);
export const deleteContactInquiryNote = (id, noteId) =>
  api.delete(`/admin/contact-inquiries/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`);
export const deleteContactInquiry = (id, data) =>
  api.post(`/admin/contact-inquiries/${encodeURIComponent(id)}/delete`, data);
// Replying is sendPortalEmail with the contact_inquiries audience — one email
// system, one composer. There is deliberately no separate reply endpoint.

// Newsletter subscribers — the public opt-in list.
export const getNewsletterSubscribers = (params) =>
  api.get('/admin/newsletter-subscribers', { params });
export const getNewsletterSubscriber = (id) =>
  api.get(`/admin/newsletter-subscribers/${encodeURIComponent(id)}`);
// Unsubscribe or reactivate. Reactivating records fresh consent evidence
// attributed to the staff member who did it.
export const updateNewsletterSubscriber = (id, data) =>
  api.patch(`/admin/newsletter-subscribers/${encodeURIComponent(id)}`, data);
// Exactly the rows the current filters are showing, as CSV.
export const exportNewsletterSubscribers = (params) =>
  api.get('/admin/newsletter-subscribers/export', { params, responseType: 'blob' });

// ─── MCC Event Studio ───
// Events, their registrations, brochures and the versioned legal policies a
// registration is evidence against.
export const getEvents = (params) => api.get('/admin/events', { params });
export const getEvent = (id) => api.get(`/admin/events/${id}`);
export const createEvent = (data) => api.post('/admin/events', data);
export const updateEvent = (id, data) => api.patch(`/admin/events/${id}`, data);
export const changeEventStatus = (id, data) => api.post(`/admin/events/${id}/status`, data);
// Any event can be deleted; `confirm` is what a published event or one with
// registrations needs before the server will do it.
export const deleteEvent = (id, { confirm = false } = {}) =>
  api.delete(`/admin/events/${id}`, { params: confirm ? { confirm: true } : {} });
export const duplicateEvent = (id, data) => api.post(`/admin/events/${id}/duplicate`, data);
export const previewEvent = (id, locale) =>
  api.get(`/admin/events/${id}/preview`, { params: locale ? { locale } : {} });
export const updateFeaturedEventOrder = (items) => api.put('/admin/events/featured-order', { items });
export const getEventAudit = (id) => api.get(`/admin/events/${id}/audit`);
export const getEventRegistrations = (id, params) =>
  api.get(`/admin/events/${id}/registrations`, { params });
// Identity and routing. The staff reference code is internal vocabulary; the
// public route is what the marketing site serves. Neither is the other.
export const getEventReferenceCodeSuggestion = (params) =>
  api.get('/admin/events/reference-code-suggestion', { params });
// The program tags an event can be filed under — the managed catalog in
// backend/event_programs.py merged with the portal's own active programs.
export const getEventProgramOptions = () => api.get('/admin/events/program-options');
export const checkEventPublicRoute = (id) => api.post(`/admin/events/${id}/public-route/check`);
export const releaseEventAlias = (id, data) => api.post(`/admin/events/${id}/aliases/release`, data);

// Agency share links. One tracked address per agency per channel: the portal
// generates it, the agency shares it, and the clicks and registrations it
// earned come back on the same record.
export const getEventAgencyLinks = (id, params) =>
  api.get(`/admin/events/${id}/agency-links`, { params });
export const createEventAgencyLink = (id, data) =>
  api.post(`/admin/events/${id}/agency-links`, data);
export const updateEventAgencyLink = (id, linkId, data) =>
  api.patch(`/admin/events/${id}/agency-links/${linkId}`, data);
export const deleteEventAgencyLink = (id, linkId, params) =>
  api.delete(`/admin/events/${id}/agency-links/${linkId}`, { params });
export const getEventAgencyLinkQr = (id, linkId) =>
  api.get(`/admin/events/${id}/agency-links/${linkId}/qr`, { responseType: 'blob' });
export const getAgencyEventLinks = (params) => api.get('/admin/agency-event-links', { params });

// Brochures — phase one uploads and the Brochure Studio.
export const getBrochureTemplates = () => api.get('/admin/brochure-studio/templates');
export const getEventBrochures = (id) => api.get(`/admin/events/${id}/brochures`);
export const createBrochureUploadUrl = (id, data) =>
  api.post(`/admin/events/${id}/brochures/uploads`, data);
export const attachEventBrochure = (id, data) => api.post(`/admin/events/${id}/brochures`, data);
export const generateEventBrochure = (id, data) =>
  api.post(`/admin/events/${id}/brochures/generate`, data);
export const previewEventBrochure = (id, data) =>
  api.post(`/admin/events/${id}/brochures/preview`, data);
export const updateEventBrochure = (id, brochureId, data) =>
  api.patch(`/admin/events/${id}/brochures/${brochureId}`, data);
export const deleteEventBrochure = (id, brochureId, { confirm } = {}) =>
  api.delete(`/admin/events/${id}/brochures/${brochureId}`, {
    params: confirm ? { confirm } : undefined,
  });
export const getEventBrochurePreview = (id, brochureId) =>
  api.get(`/admin/events/${id}/brochures/${brochureId}/preview`, { responseType: 'blob' });
export const getEventBrochureDownload = (id, brochureId) =>
  api.get(`/admin/events/${id}/brochures/${brochureId}/download`);

/**
 * Upload one brochure to R2 and attach it to an event.
 *
 * Same three steps as lesson media: presign a PUT, send the raw file to R2,
 * then confirm the attach so the server verifies the real object before it
 * records the key.
 */
export async function uploadEventBrochure(eventId, file, {
  title,
  description,
  alt_text,
  locale,
  display_order,
  replaces_brochure_id,
  onProgress,
} = {}) {
  const { data: presigned } = await createBrochureUploadUrl(eventId, {
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size: file.size,
  });

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.upload_url, true);
    xhr.setRequestHeader('Content-Type', presigned.content_type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () =>
      (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });

  return attachEventBrochure(eventId, {
    key: presigned.key,
    title: title || file.name,
    description: description || '',
    alt_text: alt_text || '',
    locale: locale || 'en',
    display_order: Number(display_order || 0),
    replaces_brochure_id: replaces_brochure_id || undefined,
    content_type: presigned.content_type,
  });
}

// Legal policies — versioned, and published versions are immutable.
export const getLegalPolicies = (params) => api.get('/admin/legal/policies', { params });
export const getLegalPolicy = (id) => api.get(`/admin/legal/policies/${id}`);
export const createLegalPolicy = (data) => api.post('/admin/legal/policies', data);
export const updateLegalPolicy = (id, data) => api.patch(`/admin/legal/policies/${id}`, data);
export const changeLegalPolicyStatus = (id, data) =>
  api.post(`/admin/legal/policies/${id}/status`, data);
export const getLegalPolicyHistory = (id) => api.get(`/admin/legal/policies/${id}/history`);

// Section layouts for the student and agency profile pages, shared by all admins.
export const getProfileLayout = (surface) => api.get(`/admin/profile-layouts/${surface}`);
export const updateProfileLayout = (surface, data) => api.put(`/admin/profile-layouts/${surface}`, data);
export const resetProfileLayout = (surface) => api.delete(`/admin/profile-layouts/${surface}`);
export const getAgencyTableViews = () => api.get('/admin/agency-table-views');
export const createAgencyTableView = (data) => api.post('/admin/agency-table-views', data);
export const updateAgencyTableView = (id, data) => api.put(`/admin/agency-table-views/${id}`, data);
export const deleteAgencyTableView = (id) => api.delete(`/admin/agency-table-views/${id}`);
export const getAgencyFields = () => api.get('/admin/agency-fields');
export const createAgencyCustomField = (data) => api.post('/admin/agency-fields', data);
export const updateAgencyCustomField = (id, data) => api.patch(`/admin/agency-fields/${id}`, data);
export const deleteAgencyCustomField = (id) => api.delete(`/admin/agency-fields/${id}`);
export const getAgentsMeta = () => api.get('/agents/meta');
export const getAgent = (id) => api.get(`/agents/${id}`);
export const uploadAgentDocument = (id, data) =>
  api.post(`/agents/${id}/documents`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getAgentDocumentDownload = (agentId, documentId) =>
  api.get(`/agents/${agentId}/documents/${documentId}/url`);
export const deleteAgentDocument = (agentId, documentId) =>
  api.delete(`/agents/${agentId}/documents/${documentId}`);
export const getAgentDashboardLayout = (id) => api.get(`/agents/${id}/dashboard-layout`);
export const updateAgentDashboardLayout = (id, data) => api.put(`/agents/${id}/dashboard-layout`, data);
export const resetAgentDashboardLayout = (id) => api.delete(`/agents/${id}/dashboard-layout`);
export const revealAgentSin = (id) => api.get(`/agents/${id}/sin`);
export const createAgent = (data) => api.post('/agents', data);
export const updateAgent = (id, data) => api.put(`/agents/${id}`, data);
export const patchAgent = (id, data) => api.patch(`/agents/${id}`, data);
export const deleteAgent = (id) => api.delete(`/agents/${id}`);
export const acceptAgentApplication = (id) => api.post(`/agents/${id}/accept-application`);
export const linkStudentToAgent = (agentId, studentId) =>
  api.post(`/agents/${agentId}/link-student/${studentId}`);
export const unlinkStudentFromAgent = (agentId, studentId) =>
  api.post(`/agents/${agentId}/unlink-student/${studentId}`);
export const addAgentNote = (agentId, data) => api.post(`/agents/${agentId}/notes`, data);
export const updateAgentNote = (agentId, noteId, data) =>
  api.put(`/agents/${agentId}/notes/${noteId}`, data);
export const deleteAgentNote = (agentId, noteId) =>
  api.delete(`/agents/${agentId}/notes/${noteId}`);
export const getAgentCommissionStudents = (agentId) =>
  api.get(`/agents/${agentId}/commission-students`);
export const getAgentCommissionPayments = (agentId) =>
  api.get(`/agents/${agentId}/commission-payments`);
export const getAdminCommissions = (params) => api.get('/commissions', { params });
export const createAgentCommissionPayment = (agentId, data) =>
  api.post(`/agents/${agentId}/commission-payments`, data);
export const deleteCommissionPayment = (paymentId) =>
  api.delete(`/commission-payments/${paymentId}`);
export const deleteCommissionPaymentBatch = (batchId) =>
  api.delete(`/commission-payments/batch/${batchId}`);
export const getAgentCommissionCheques = (agentId) => api.get(`/agents/${agentId}/commission-cheques`);
export const getAgentCommissionInvoices = (agentId, studentId) => api.get(`/agents/${agentId}/commission-invoices`, { params: { student_id: studentId } });
export const createInvoiceCommissionCheque = (agentId, data) => api.post(`/agents/${agentId}/invoice-commission-cheques`, data);
export const createAgentCommissionCheque = (agentId, paymentIds) =>
  api.post(`/agents/${agentId}/commission-cheques`, { payment_ids: paymentIds });
export const getOperatingPayments = () => api.get('/operating-payments');
export const createOperatingPayment = (data) => api.post('/operating-payments', data);
export const deleteOperatingPayment = (id) => api.delete(`/operating-payments/${id}`);
export const getChequeRenderHtml = (id) => api.get(`/cheques/${id}/render-html`);
export const saveCheque = (id, fields) => api.post(`/cheques/${id}/save`, { fields });
export const resetChequeEdits = (id) => api.post(`/cheques/${id}/reset-edits`, {});
export const updateChequeStatus = (id, status, reason = '') => api.patch(`/cheques/${id}/status`, { status, reason });
export const deleteCheque = (id) => api.delete(`/cheques/${id}`);
export const downloadCheque = (id) => api.get(`/cheques/${id}/download`, { responseType: 'blob' });
export const createAgentPortalAccount = (agentId, data) =>
  api.post(`/agents/${agentId}/portal-account`, data);
export const getAgentAgreement = (agentId) => api.get(`/agents/${agentId}/agreement`);

// Programs - admin-only.
export const getPrograms = (params) => api.get('/programs', { params });
export const createProgram = (data) => api.post('/programs', data);
export const updateProgram = (id, data) => api.put(`/programs/${id}`, data);
export const deleteProgram = (id) => api.delete(`/programs/${id}`);

// Instructors
export const getInstructors = () => api.get('/instructors');

// Submissions
export const getSubmissions = (params) => api.get('/submissions', { params });
export const submitExam = (data) => api.post('/submissions/exam', data);
export const submitAssignment = (data) => api.post('/submissions/assignment', data);

// Student submission attachments. Same presign → PUT → confirm exchange the
// teaching side uses, so file bytes never pass through the API.
export const createSubmissionUploadUrl = (data) => api.post('/submissions/uploads', data);
export const getSubmissionAttachmentUrl = (submissionId, index) =>
  api.get(`/submissions/${submissionId}/attachments/${index}/download`);

/**
 * Upload one file a student is attaching to an assignment.
 *
 * Returns the attachment descriptor to send with the submission — the file is
 * only recorded against the submission when the student actually hands in.
 */
export async function uploadSubmissionFile(assignmentId, file, { onProgress } = {}) {
  const { data: presigned } = await createSubmissionUploadUrl({
    assignment_id: assignmentId,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    size: file.size,
  });

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.upload_url, true);
    xhr.setRequestHeader('Content-Type', presigned.content_type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () =>
      (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed — the file could not be sent to storage.'));
    xhr.send(file);
  });

  return { key: presigned.key, filename: file.name, content_type: presigned.content_type };
}
export const gradeSubmission = (id, data) => api.put(`/submissions/${id}/grade`, data);
export const deleteSubmission = (id) => api.delete(`/submissions/${id}`);

// Progress
export const completeLesson = (params) => api.post('/progress/complete-lesson', null, { params });
export const getProgress = (studentId) => api.get(`/progress/${studentId}`);
export const getDetailedProgress = (studentId) => api.get(`/progress/${studentId}/detailed`);

// Exam Review
export const getExamReview = (examId, studentId) => api.get(`/exams/${examId}/review/${studentId}`);

// Reports
export const getReportExportUrl = (report, format) => `${API}/reports/${report}/${format}`;
export const getEnrollmentsCSVUrl = () => getReportExportUrl('enrollments', 'csv');
export const getEnrollmentsPDFUrl = () => getReportExportUrl('enrollments', 'pdf');
export const getGradesCSVUrl = () => getReportExportUrl('grades', 'csv');
export const getGradesPDFUrl = () => getReportExportUrl('grades', 'pdf');
export const getSubmissionsCSVUrl = () => getReportExportUrl('submissions', 'csv');
export const getSubmissionsPDFUrl = () => getReportExportUrl('submissions', 'pdf');
export const downloadEnrollmentsCsv = () => triggerDownload(getEnrollmentsCSVUrl(), 'enrollments.csv');
export const downloadEnrollmentsPdf = () => triggerDownload(getEnrollmentsPDFUrl(), 'enrollments.pdf');
export const downloadGradesCsv = () => triggerDownload(getGradesCSVUrl(), 'grades.csv');
export const downloadGradesPdf = () => triggerDownload(getGradesPDFUrl(), 'grades.pdf');
export const downloadSubmissionsCsv = () => triggerDownload(getSubmissionsCSVUrl(), 'submissions.csv');
export const downloadSubmissionsPdf = () => triggerDownload(getSubmissionsPDFUrl(), 'submissions.pdf');
export const downloadStudentsCsv = () => triggerDownload(getStudentsExportUrl('csv'), 'students_export.csv');
export const downloadStudentsPdf = () => triggerDownload(getStudentsExportUrl('pdf'), 'students_export.pdf');

// Course Actions
export const archiveCourse = (id) => api.put(`/courses/${id}/archive`);
export const restoreCourse = (id) => api.put(`/courses/${id}/restore`);
export const bulkActionCourses = (data) => api.post('/courses/bulk-action', data);
export const getCourseTerms = () => api.get('/courses/terms');

// Course Grades
export const getCourseGrades = (params) => api.get('/course-grades', { params });
export const createCourseGrade = (data) => api.post('/course-grades', data);
export const updateCourseGrade = (id, data) => api.put(`/course-grades/${id}`, data);
export const deleteCourseGrade = (id) => api.delete(`/course-grades/${id}`);
export const getStudentGPA = (studentId) => api.get(`/course-grades/student/${studentId}/gpa`);

// Analytics
export const getAnalyticsOverview = () => api.get('/analytics/overview');

// AI Marketing - admin-only.
export const getMarketingOverview = () => api.get('/marketing/overview');
export const generateMarketingContent = (data) => api.post('/marketing/ai/generate', data);
export const getMarketingAiSettings = () => api.get('/marketing/ai/settings');
export const updateMarketingAiSettings = (data) => api.put('/marketing/ai/settings', data);
export const generateCampaignPosts = (id, data) => api.post(`/marketing/campaigns/${id}/generate-posts`, data);
export const getMarketingCampaigns = (params) => api.get('/marketing/campaigns', { params });
export const createMarketingCampaign = (data) => api.post('/marketing/campaigns', data);
export const updateMarketingCampaign = (id, data) => api.patch(`/marketing/campaigns/${id}`, data);
export const deleteMarketingCampaign = (id) => api.delete(`/marketing/campaigns/${id}`);
export const getMarketingContent = (params) => api.get('/marketing/content', { params });
export const createMarketingContent = (data) => api.post('/marketing/content', data);
export const bulkCreateMarketingContent = (items) => api.post('/marketing/content/bulk', { items });
export const updateMarketingContent = (id, data) => api.patch(`/marketing/content/${id}`, data);
export const shareMarketingContent = (id) => api.post(`/marketing/content/${id}/share`);
export const deleteMarketingContent = (id) => api.delete(`/marketing/content/${id}`);

// Marketing automation - full-admin only. The rules that decide, on their own,
// that a lead or applicant should be contacted.
export const getAutomationCatalog = () => api.get('/marketing/automations/catalog');
export const getAutomations = (params) => api.get('/marketing/automations', { params });
export const getAutomation = (id) => api.get(`/marketing/automations/${id}`);
export const createAutomation = (data) => api.post('/marketing/automations', data);
export const updateAutomation = (id, data) => api.put(`/marketing/automations/${id}`, data);
export const setAutomationStatus = (id, status) => api.post(`/marketing/automations/${id}/status`, { status });
export const deleteAutomation = (id) => api.delete(`/marketing/automations/${id}`);
export const previewAutomation = (id, recipientIds) =>
  api.post(`/marketing/automations/${id}/preview`, { recipient_ids: recipientIds });
export const runAutomation = (id, recipientIds) =>
  api.post(`/marketing/automations/${id}/run`, { recipient_ids: recipientIds, confirm: true });
export const getAutomationAudience = (id) => api.get(`/marketing/automations/${id}/audience`);
export const runAutomationOverAudience = (id) =>
  api.post(`/marketing/automations/${id}/run-audience`, { confirm: true });
export const getAutomationRuns = (params) => api.get('/marketing/automations/runs', { params });
export const retryAutomationRun = (runId) => api.post(`/marketing/automations/runs/${runId}/retry`);
export const cancelAutomationRun = (runId) => api.post(`/marketing/automations/runs/${runId}/cancel`);
export const processDueAutomations = () => api.post('/marketing/automations/process-due');

// Native MCC Accounting. Sales and Payments read the portal's own records —
// students, applications, agencies, issued invoices, the tuition ledger — and
// never contact an external accounting service.
export const getAccountingOverview = () => api.get('/admin/accounting/overview');

export const getAccountingTaxes = () => api.get('/admin/accounting/taxes');
export const createAccountingTax = (data) => api.post('/admin/accounting/taxes', data);

export const getAccountingItems = (params) => api.get('/admin/accounting/items', { params });
export const getAccountingItem = (id) => api.get(`/admin/accounting/items/${id}`);
export const createAccountingItem = (data) => api.post('/admin/accounting/items', data);
export const updateAccountingItem = (id, data) => api.patch(`/admin/accounting/items/${id}`, data);
export const archiveAccountingItem = (id) => api.post(`/admin/accounting/items/${id}/archive`);
export const restoreAccountingItem = (id) => api.post(`/admin/accounting/items/${id}/restore`);
// Only an item no document has ever used. One that has been invoiced is
// archived instead, and the server says so rather than deleting it.
export const deleteAccountingItem = (id) => api.delete(`/admin/accounting/items/${id}`);

export const getAccountingCustomers = (params) => api.get('/admin/accounting/customers', { params });
export const getAccountingCustomer = (ref) =>
  api.get(`/admin/accounting/customers/${encodeURIComponent(ref)}`);
export const createAccountingCustomer = (data) => api.post('/admin/accounting/customers', data);
export const updateAccountingCustomer = (ref, data) =>
  api.patch(`/admin/accounting/customers/${encodeURIComponent(ref)}`, data);
// Manual customers only. A student or applicant is a portal record and is
// removed from their own profile.
export const deleteAccountingCustomer = (ref) =>
  api.delete(`/admin/accounting/customers/${encodeURIComponent(ref)}`);
export const accountingCustomersExportUrl = () => `${API}/admin/accounting/customers/export`;

export const getAccountingInvoices = (params) => api.get('/admin/accounting/invoices', { params });
export const getAccountingInvoiceSummary = (params) =>
  api.get('/admin/accounting/invoices/summary', { params });
export const getAccountingInvoice = (ref) =>
  api.get(`/admin/accounting/invoices/${encodeURIComponent(ref)}`);
export const createAccountingInvoice = (data) => api.post('/admin/accounting/invoices', data);
export const updateAccountingInvoice = (ref, data) =>
  api.patch(`/admin/accounting/invoices/${encodeURIComponent(ref)}`, data);
export const reopenAccountingInvoice = (ref) => api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/reopen`);
export const restoreAccountingInvoiceRevision = (ref, revisionId) => api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/revisions/${encodeURIComponent(revisionId)}/restore`);
export const issueAccountingInvoice = (ref) =>
  api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/issue`);
export const voidAccountingInvoice = (ref) =>
  api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/void`);
// Accounting-raised invoices that no payment names. Application invoices and
// paid invoices are refused with the reason.
export const deleteAccountingInvoice = (ref) =>
  api.delete(`/admin/accounting/invoices/${encodeURIComponent(ref)}`);
export const updateAccountingInvoicePayment = (ref, data) =>
  api.patch(`/admin/accounting/invoice-payments/${encodeURIComponent(ref)}`, data);
export const deleteAccountingInvoicePayment = (ref) =>
  api.delete(`/admin/accounting/invoice-payments/${encodeURIComponent(ref)}`);
export const recordAccountingInvoicePayment = (ref, data) =>
  api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/payments`, data);
// The invoice as its document. A stored PDF comes back as {mode:'url'}; a
// natively raised invoice is rendered now and streamed, so the caller has to be
// ready for either.
export const getAccountingInvoiceDocument = (ref) =>
  api.get(`/admin/accounting/invoices/${encodeURIComponent(ref)}/document`, {
    responseType: 'blob',
  });

export const sendAccountingInvoiceReminder = (ref, data) =>
  api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/reminder`, data);

// Raise a new invoice from an existing one. The response is the *new* invoice,
// as a draft with its own number — never a mutation of the original.
export const duplicateAccountingInvoice = (ref) =>
  api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/duplicate`);
// The invoice rendered as HTML from the same context the PDF uses, so the
// preview on the invoice page cannot drift from the document that is sent.
export const getAccountingInvoicePreview = (ref) =>
  api.get(`/admin/accounting/invoices/${encodeURIComponent(ref)}/preview`);
export const getAccountingInvoiceActivity = (ref) =>
  api.get(`/admin/accounting/invoices/${encodeURIComponent(ref)}/activity`);
// The email composer: its opening state, the preview of what will be sent, and
// the send itself. The preview is rendered by the same code that sends.
export const getAccountingInvoiceEmail = (ref) =>
  api.get(`/admin/accounting/invoices/${encodeURIComponent(ref)}/email`);
export const previewAccountingInvoiceEmail = (ref, data) =>
  api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/email/preview`, data);
export const sendAccountingInvoiceEmail = (ref, data) =>
  api.post(`/admin/accounting/invoices/${encodeURIComponent(ref)}/email`, data);

export const getAccountingPayees = (params) => api.get('/admin/accounting/payees', { params });
export const createAccountingPayee = (data) => api.post('/admin/accounting/payees', data);
export const deleteAccountingPayee = (id) => api.delete(`/admin/accounting/payees/${id}`);

export const getAccountingBills = (params) => api.get('/admin/accounting/bills', { params });
export const getAccountingBill = (ref) =>
  api.get(`/admin/accounting/bills/${encodeURIComponent(ref)}`);
export const createAccountingBill = (data) => api.post('/admin/accounting/bills', data);
export const updateAccountingBill = (ref, data) =>
  api.patch(`/admin/accounting/bills/${encodeURIComponent(ref)}`, data);
export const openAccountingBill = (ref) =>
  api.post(`/admin/accounting/bills/${encodeURIComponent(ref)}/open`);
export const voidAccountingBill = (ref) =>
  api.post(`/admin/accounting/bills/${encodeURIComponent(ref)}/void`);
export const deleteAccountingBill = (ref) =>
  api.delete(`/admin/accounting/bills/${encodeURIComponent(ref)}`);
export const recordAccountingBillPayment = (ref, data) =>
  api.post(`/admin/accounting/bills/${encodeURIComponent(ref)}/payments`, data);

// Accounting → Transactions. The list is a projection over the portal's own
// money movements plus the ones entered by hand, so a `ref` here may name a
// manual transaction (`txn:`) or a view of a record another workflow owns
// (`tuition:`, `billpay:`, `commission:`, `cheque:`). What a call is allowed to
// change depends on which, and the server is what decides.
export const getAccountingTransactions = (params) =>
  api.get('/admin/accounting/transactions', { params });
export const getAccountingTransaction = (ref) =>
  api.get(`/admin/accounting/transactions/${encodeURIComponent(ref)}`);
export const createAccountingTransaction = (data) =>
  api.post('/admin/accounting/transactions', data);
export const updateAccountingTransaction = (ref, data) =>
  api.patch(`/admin/accounting/transactions/${encodeURIComponent(ref)}`, data);
export const reviewAccountingTransaction = (ref, reviewed) =>
  api.post(`/admin/accounting/transactions/${encodeURIComponent(ref)}/review`, { reviewed });
export const deleteAccountingTransaction = (ref) =>
  api.delete(`/admin/accounting/transactions/${encodeURIComponent(ref)}`);

// The chart behind Transactions: the accounts that hold money, and the
// categories that classify it. One collection, two jobs.
export const getAccountingLedgerAccounts = (params) =>
  api.get('/admin/accounting/ledger-accounts', { params });
export const createAccountingLedgerAccount = (data) =>
  api.post('/admin/accounting/ledger-accounts', data);
export const updateAccountingLedgerAccount = (id, data) =>
  api.patch(`/admin/accounting/ledger-accounts/${id}`, data);
export const archiveAccountingLedgerAccount = (id) =>
  api.post(`/admin/accounting/ledger-accounts/${id}/archive`);
export const restoreAccountingLedgerAccount = (id) =>
  api.post(`/admin/accounting/ledger-accounts/${id}/restore`);

// Paginated Submissions
export const getSubmissionsPaginated = (params) => api.get('/reports/submissions/list', { params });

// Announcements
export const getAnnouncements = (params) => api.get('/announcements', { params });
export const createAnnouncement = (data) => api.post('/announcements', data);
export const updateAnnouncement = (id, data) => api.put(`/announcements/${id}`, data);
export const deleteAnnouncement = (id) => api.delete(`/announcements/${id}`);

// Dashboard
export const getStudentDashboard = (studentId) => api.get(`/dashboard/student/${studentId}`);
export const getAdminDashboard = () => api.get('/dashboard/admin');
export const getAdminPeriodStats = (params) => api.get('/dashboard/admin/period-stats', { params });
export const getInstructorDashboard = (instructorId) => api.get(`/dashboard/instructor/${instructorId}`);

// Document PDFs
export const getOfferOfAdmissionsUrl = (studentId) => `${API}/documents/offer-of-admissions/${studentId}`;
export const getLOAUrl = (studentId) => `${API}/documents/loa/${studentId}`;
export const getTranscriptUrl = (studentId) => `${API}/documents/transcript/${studentId}`;
export const getUnofficialTranscriptUrl = (studentId) => `${API}/documents/transcript/${studentId}/unofficial`;
export const getStudentDocuments = (studentId) => api.get(`/students/${studentId}/documents`);
export const getStudentDocumentDefaults = (studentId, docType) =>
  api.get(`/students/${studentId}/documents/${docType}/defaults`);
export const issueStudentDocument = (studentId, data) => api.post(`/students/${studentId}/documents/issue`, data);
export const getApplicationIssuedDocuments = (applicationId) =>
  api.get(`/applications/${applicationId}/issued-documents`);
export const getApplicationIssuedDocumentDefaults = (applicationId, docType) =>
  api.get(`/applications/${applicationId}/issued-documents/${docType}/defaults`);
export const issueApplicationDocument = (applicationId, data) =>
  api.post(`/applications/${applicationId}/issued-documents/issue`, data);
export const getDocumentRenderHtml = (documentId) => api.get(`/documents/${documentId}/render-html`);
export const saveDocument = (documentId, fields) => api.post(`/documents/${documentId}/save`, { fields });
export const getApplicationDocumentRenderHtml = getDocumentRenderHtml;
export const saveApplicationDocument = saveDocument;
// Backwards-compatible aliases for the Offer editor introduced in Doc 25.
export const getOfferDocumentRenderHtml = getApplicationDocumentRenderHtml;
export const saveOfferDocument = saveApplicationDocument;
export const confirmIssuedDocument = (documentId) => api.post(`/documents/${documentId}/confirm`);
export const reopenIssuedDocument = (documentId) => api.post(`/documents/${documentId}/reopen`);
export const restoreIssuedDocument = (documentId) => api.post(`/documents/${documentId}/restore`);
export const revokeIssuedDocument = (documentId) => api.post(`/documents/${documentId}/revoke`);
export const getIssuedDocumentDownload = (documentId) => api.get(`/documents/${documentId}/download`);

// Seed
export const seedDatabase = () => api.post('/seed');

// Auth (native)
export const authLogin = (data) => api.post('/auth/login', data);
export const authRegister = (data) => api.post('/auth/register', data);
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (data) => api.post('/auth/reset-password', data);
export const authMe = () => api.get('/auth/me');
export const getAdminAccounts = () => api.get('/admin/accounts');
export const createAdminAccount = (data) => api.post('/admin/accounts', data);
export const updateAdminAccount = (id, data) => api.patch(`/admin/accounts/${id}`, data);
export const deleteAdminAccount = (id) => api.delete(`/admin/accounts/${id}`);
export const updateMyProfile = (data) => api.patch('/me/profile', data);
export const changeMyPassword = (data) => api.patch('/me/password', data);

// ─── /api/me/* — role-scoped, identity from JWT ─────────────────────────
// Backend derives the calling student/instructor from the bearer token, so
// none of these accept an id argument.
export const getMe = () => api.get('/me');
export const getMyDashboard = () => api.get('/me/dashboard');
export const getMyCourses = () => api.get('/me/courses');
export const getMyCourseWorkspace = (courseId) => api.get(`/me/courses/${courseId}/workspace`);
export const getMyCourseGradebook = (courseId) => api.get(`/me/courses/${courseId}/gradebook`);
export const saveMyCourseGrade = (courseId, data) => api.put(`/me/courses/${courseId}/gradebook`, data);
export const saveMyCourseAttendance = (courseId, data) => api.put(`/me/courses/${courseId}/attendance`, data);
export const getMyAssignments = () => api.get('/me/assignments');
export const getMyExams = () => api.get('/me/exams');
export const getMyLiveSessions = () => api.get('/me/live-sessions');
export const getMySubmissions = () => api.get('/me/submissions');
export const getMyGrades = () => api.get('/me/grades');
export const getMyFinances = () => api.get('/me/finances');
export const getMyTuitionPayments = () => api.get('/me/tuition-payments');
export const getTuitionPayments = (studentId) => api.get(`/students/${studentId}/tuition-payments`);
export const createTuitionPayment = (studentId, data) => api.post(`/students/${studentId}/tuition-payments`, data);
export const updateTuitionPayment = (paymentId, data) => api.patch(`/tuition-payments/${paymentId}`, data);
export const deleteTuitionPayment = (paymentId) => api.delete(`/tuition-payments/${paymentId}`);
export const getMyProgress = () => api.get('/me/progress');
export const getMyProgressDetailed = () => api.get('/me/progress/detailed');
export const getMyAnnouncements = () => api.get('/me/announcements');
export const getMyStudents = () => api.get('/me/students');
export const getMyDocuments = () => api.get('/me/documents');
export const getMyDocumentDownload = (docType) => api.get(`/me/documents/${docType}/download`);
export const getMyAgencyProfile = () => api.get('/me/agency/profile');
export const getMyAgencyStudents = () => api.get('/me/agency/students');
export const getMyAgencyApplications = () => api.get('/me/agency/applications');
export const getMyAgencyCommissions = () => api.get('/me/agency/commissions');
export const getMyAgencyDocuments = () => api.get('/me/agency/documents');
export const getMyAgencyDocumentDownload = (documentId) =>
  api.get(`/me/agency/documents/${documentId}/url`);
export const getMyAgencyAgreement = () => api.get('/me/agency/agreement');
export const getMyAgencyEventLinks = () => api.get('/me/agency/event-links');
// ─── Language tests ───
// English proficiency and French placement, one section and one collection.
// Sending, amending and deleting live here; a person's profile only reads.
export const getLanguageTestCatalog = () => api.get('/admin/language-tests/catalog');
export const getLanguageTests = (params) => api.get('/admin/language-tests', { params });
export const getLanguageTest = (id) => api.get(`/admin/language-tests/${id}`);
export const sendLanguageTest = (data) => api.post('/admin/language-tests', data);
// Amend a marked result: a score, a level, or `revert` to put the automatic
// marking back. A reason is required for anything but a revert.
export const updateLanguageTestResult = (id, data) => api.patch(`/admin/language-tests/${id}`, data);
export const deleteLanguageTest = (id) => api.delete(`/admin/language-tests/${id}`);
// Who else is copied on the result. Its own call because adding a referring
// agency is not amending anybody's mark.
export const updateLanguageTestCopies = (id, data) =>
  api.patch(`/admin/language-tests/${id}/copies`, data);
// Read-only, for the profile panel.
export const getLanguageTestsFor = (targetType, targetId) =>
  api.get(`/${targetType === 'application' ? 'applications' : 'students'}/${targetId}/language-tests`);
// The candidate's own view. `/language-test/:token` is the current address;
// `/english-assessment/:token` still resolves for links already sent out.
export const getPublicLanguageTest = (token) => api.get(`/public/language-tests/${token}`);
export const submitPublicLanguageTest = (token, answers) => api.post(`/public/language-tests/${token}/submit`, { answers });
export const getMyNotifications = (params) => api.get('/me/notifications', { params });
export const markNotificationRead = (id) => api.post(`/me/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/me/notifications/read-all');
export const getInboxSummary = () => api.get('/me/inbox/summary');
export const notificationActions = (data) => api.post('/me/notifications/actions', data);
export const getNotificationDetail = (id) => api.get(`/me/notifications/${encodeURIComponent(id)}/detail`);
export const replyToNotification = (id, data) => api.post(`/me/notifications/${encodeURIComponent(id)}/reply`, data);
export const setThreadFollowing = (id, following) => api.patch(`/me/notifications/${encodeURIComponent(id)}/thread`, { following });
export const getNotificationPreferences = () => api.get('/me/notification-preferences');
export const getNotificationEmailStatus = () => api.get('/me/notification-email-status');
export const sendNotificationEmailTest = () => api.post('/me/notification-email-test');
export const saveNotificationPreferences = (categories, email_categories) => api.patch('/me/notification-preferences', { categories, ...(email_categories ? { email_categories } : {}) });
export const getInboxTeammates = (params) => api.get('/me/inbox/teammates', { params });
export const updateChatState = (id, data) => api.patch(`/admin/chat/channels/${encodeURIComponent(id)}/state`, data);

// Document Services — authenticated student endpoints. Identity always comes
// from the bearer token; callers never pass a student id. Prices and totals are
// deliberately absent from mutation payloads and are recalculated by the API.
export const getMyDocumentServicesConfig = () => api.get('/me/document-services/config');
export const getMyServiceProducts = (params) => api.get('/me/document-services/products', { params });
export const getMyServiceProduct = (id) => api.get(`/me/document-services/products/${encodeURIComponent(id)}`);
export const validateMyServiceProductForm = (id, data) =>
  api.post(`/me/document-services/products/${encodeURIComponent(id)}/validate`, data);
export const createMyServiceOrder = (data) => api.post('/me/document-services/orders', data);
export const getMyServiceOrders = (params) => api.get('/me/document-services/orders', { params });
export const getMyServiceOrder = (id) => api.get(`/me/document-services/orders/${encodeURIComponent(id)}`);
export const startMyServiceCheckout = (id, idempotencyKey) =>
  api.post(`/me/document-services/orders/${encodeURIComponent(id)}/checkout`, {
    idempotency_key: idempotencyKey,
  });
export const verifyMyServicePayment = (id, ticket) =>
  api.post(`/me/document-services/orders/${encodeURIComponent(id)}/payment/verify`, { ticket });
export const cancelMyServicePayment = (id, ticket) =>
  api.post(`/me/document-services/orders/${encodeURIComponent(id)}/payment/cancel`, { ticket });
export const getMyServiceTracking = (id) =>
  api.get(`/me/document-services/orders/${encodeURIComponent(id)}/tracking`);
export const getMyServiceReceiptUrl = (id, document = 'receipt') =>
  `${API}/me/document-services/orders/${encodeURIComponent(id)}/${document === 'invoice' ? 'invoice' : 'receipt'}`;
export const getMyShippingUpgrade = (parentOrderId, params = {}) =>
  api.get(`/me/document-services/orders/${encodeURIComponent(parentOrderId)}/shipping-upgrades`, { params });
export const createMyShippingUpgrade = (parentOrderId, data) =>
  api.post(`/me/document-services/orders/${encodeURIComponent(parentOrderId)}/shipping-upgrades`, data);

// Document Services — finance administration endpoints.
export const getAdminDocumentServicesConfig = () => api.get('/admin/document-services/configuration');
export const updateAdminDocumentServicesConfig = (data) => api.patch('/admin/document-services/configuration', data);
export const getAdminServiceOrders = (params) => api.get('/admin/document-services/orders', { params });
export const getAdminServiceOrder = (id) => api.get(`/admin/document-services/orders/${encodeURIComponent(id)}`);
export const updateAdminServiceOrderStatus = (id, data) =>
  api.patch(`/admin/document-services/orders/${encodeURIComponent(id)}/status`, data);
export const cancelAdminServiceOrder = (id, data) =>
  api.post(`/admin/document-services/orders/${encodeURIComponent(id)}/cancel`, data);
export const issueAdminServiceRefund = (id, data) =>
  api.post(`/admin/document-services/orders/${encodeURIComponent(id)}/refunds`, data);
export const confirmAdminServiceRefund = (id, refundId, data) =>
  api.post(`/admin/document-services/orders/${encodeURIComponent(id)}/refunds/${encodeURIComponent(refundId)}/confirm`, data);
export const resendAdminServiceOrderEmail = (id, data) =>
  api.post(`/admin/document-services/orders/${encodeURIComponent(id)}/emails/resend`, data);
export const reissueAdminServiceGuestLink = (id) =>
  api.post(`/admin/document-services/orders/${encodeURIComponent(id)}/guest-link/reissue`);
export const addAdminServiceOrderNote = (id, data) =>
  api.post(`/admin/document-services/orders/${encodeURIComponent(id)}/notes`, data);
export const updateAdminServiceOrderNote = (id, noteId, data) =>
  api.patch(`/admin/document-services/orders/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, data);
export const deleteAdminServiceOrderNote = (id, noteId) =>
  api.delete(`/admin/document-services/orders/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`);
export const getAdminServiceOrdersExportUrl = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  });
  return `${API}/admin/document-services/orders/export${query.toString() ? `?${query}` : ''}`;
};

export const getAdminServiceProducts = (params) => api.get('/admin/document-services/products', { params });
export const getAdminServiceProduct = (id) => api.get(`/admin/document-services/products/${encodeURIComponent(id)}`);
export const createAdminServiceProduct = (data) => api.post('/admin/document-services/products', data);
export const updateAdminServiceProduct = (id, data) =>
  api.patch(`/admin/document-services/products/${encodeURIComponent(id)}`, data);
export const duplicateAdminServiceProduct = (id) =>
  api.post(`/admin/document-services/products/${encodeURIComponent(id)}/duplicate`);
export const archiveAdminServiceProduct = (id) =>
  api.post(`/admin/document-services/products/${encodeURIComponent(id)}/archive`);
export const publishAdminServiceProduct = (id, published) =>
  api.post(`/admin/document-services/products/${encodeURIComponent(id)}/${published ? 'publish' : 'unpublish'}`);
export const reorderAdminServiceProducts = (items) =>
  api.put('/admin/document-services/products/reorder', { items });
export const createAdminServiceVariant = (productId, data) =>
  api.post(`/admin/document-services/products/${encodeURIComponent(productId)}/variants`, data);
export const updateAdminServiceVariant = (variantId, data) =>
  api.patch(`/admin/document-services/variants/${encodeURIComponent(variantId)}`, data);
export const archiveAdminServiceVariant = (variantId) =>
  api.post(`/admin/document-services/variants/${encodeURIComponent(variantId)}/archive`);
export const createAdminServiceFormVersion = (productId, fields) =>
  api.post(`/admin/document-services/products/${encodeURIComponent(productId)}/form-versions`, { fields });

export const getAdminServicePayments = (params) => api.get('/admin/document-services/payments', { params });
export const reconcileAdminServicePayment = (orderId, attemptId) =>
  api.post(`/admin/document-services/orders/${encodeURIComponent(orderId)}/payment-attempts/${encodeURIComponent(attemptId)}/reconcile`);
export const getAdminServiceFulfillments = (params) => api.get('/admin/document-services/fulfillments', { params });
export const updateAdminServiceFulfillment = (orderId, data) =>
  api.patch(`/admin/document-services/orders/${encodeURIComponent(orderId)}/fulfillment`, data);
export const getAdminServiceReconciliation = (params) =>
  api.get('/admin/document-services/reconciliation', { params });
export const retryAdminServiceAccountingSync = (id) =>
  api.post(`/admin/document-services/orders/${encodeURIComponent(id)}/accounting/retry`);
export const runAdminServiceAccountingSync = (limit = 10) =>
  api.post('/admin/document-services/accounting/run', null, { params: { limit } });

export default api;

export const getPersonFields = () => api.get("/admin/person-fields");
export const updatePersonFields = (data) => api.put("/admin/person-fields", data);

export const getProfileFlags = (kind, id) => api.get(`/admin/profile-flags/${kind}/${encodeURIComponent(id)}`);
export const updateProfileFlag = (kind, id, data) => api.put(`/admin/profile-flags/${kind}/${encodeURIComponent(id)}`, data);

export const getLanguageQuestionBank = (code) => api.get(`/admin/language-tests/banks/${code}`);
export const updateLanguageQuestionBank = (code, data) => api.put(`/admin/language-tests/banks/${code}`, data);
